/* eslint-disable react-hooks/exhaustive-deps */
// src/components/ReporteInvCliente.js

import React, {
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import moment from "moment";

import {
    Container,
    Segment,
    Table,
    Grid,
    Header,
    Icon,
    Label,
    Message,
} from "semantic-ui-react";

import {
    collection,
    getDocs,
    where,
    query,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

import Alerta from "./Alertas";
import BuscadorInput from "./BuscadorInput";

/* =====================================================
   ESTILOS
===================================================== */

const stickyTh = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#f8fafc",
    color: "#42526e",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.25,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #dfe3e8",
};

/* =====================================================
   HELPERS
===================================================== */

const normalizarTexto = (valor) => {
    return String(valor || "")
        .trim()
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

const getStatusConfig = (status) => {
    const value = normalizarTexto(status);

    const configs = {
        PREPARACION: {
            label: "PREPARACIÓN",
            color: "#fbbd08",
            background: "#fff8db",
            border: "#f4d35e",
            icon: "hourglass half",
        },

        BODEGA: {
            label: "BODEGA",
            color: "#2185d0",
            background: "#eaf4fb",
            border: "#b8d8ef",
            icon: "archive",
        },

        PACIENTE: {
            label: "PACIENTE",
            color: "#478155",
            background: "#edf5ef",
            border: "#bfd6c5",
            icon: "user",
        },

        "SERVICIO TECNICO": {
            label: "SERVICIO TÉCNICO",
            color: "#6435c9",
            background: "#f0ebfb",
            border: "#cfc0ee",
            icon: "wrench",
        },

        "TRANSITO PACIENTE": {
            label: "TRÁNSITO PACIENTE",
            color: "#159447",
            background: "#eafaf0",
            border: "#bde8c9",
            icon: "truck",
        },

        "TRANSITO ST": {
            label: "TRÁNSITO ST",
            color: "#f2711c",
            background: "#fff1e8",
            border: "#f5c6a5",
            icon: "truck",
        },

        "TRANSITO BODEGA": {
            label: "TRÁNSITO BODEGA",
            color: "#00a9a5",
            background: "#e6f8f7",
            border: "#afe1df",
            icon: "truck",
        },

        PROVEEDOR: {
            label: "PROVEEDOR",
            color: "#a333c8",
            background: "#f5eafb",
            border: "#dabce9",
            icon: "building",
        },

        "TRANSITO PROVEEDOR": {
            label: "TRÁNSITO PROVEEDOR",
            color: "#db2828",
            background: "#fceaea",
            border: "#efb7b7",
            icon: "truck",
        },

        BAJA: {
            label: "BAJA",
            color: "#767676",
            background: "#f1f1f1",
            border: "#d5d5d5",
            icon: "ban",
        },
    };

    return (
        configs[value] || {
            label: status || "SIN STATUS",
            color: "#6b778c",
            background: "#f4f5f7",
            border: "#dfe1e6",
            icon: "question circle outline",
        }
    );
};

const StatusBadge = ({ status }) => {
    const config = getStatusConfig(status);

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "4px 8px",
                borderRadius: 5,
                color: config.color,
                background: config.background,
                border: `1px solid ${config.border}`,
                fontSize: 10,
                fontWeight: 700,
                whiteSpace: "nowrap",
            }}
        >
            <Icon
                name={config.icon}
                style={{
                    margin: 0,
                    fontSize: 10,
                }}
            />

            {config.label}
        </span>
    );
};

/* =====================================================
   COMPONENTE
===================================================== */

const ReporteInvCliente = () => {
    const { users } = useContext(UserContext);

    /* =====================================================
       ESTADOS
    ===================================================== */

    const [alerta, cambiarAlerta] = useState({});
    const [estadoAlerta, cambiarEstadoAlerta] =
        useState(false);

    const [rut, setRut] = useState("");
    const [entidad, setEntidad] = useState("");

    const [clientes, setClientes] = useState([]);
    const [inventario, setInventario] = useState([]);

    const [loadingClientes, setLoadingClientes] =
        useState(false);

    const [loadingInventario, setLoadingInventario] =
        useState(false);

    // Si no lo ocupas como bloqueo global
    const confirmar = false;

    const fechaHoy = useMemo(
        () => new Date(),
        []
    );

    /* =====================================================
       CARGAR CLIENTES
    ===================================================== */

    const obtenerClientes = async () => {
        if (!users?.emp_id) {
            setClientes([]);
            return;
        }

        setLoadingClientes(true);

        try {
            const qC = query(
                collection(db, "clientes"),
                where(
                    "emp_id",
                    "==",
                    users.emp_id
                )
            );

            const snap = await getDocs(qC);

            const arr = snap.docs
                .map((d) => ({
                    ...d.data(),
                    id: d.id,
                }))
                .sort((a, b) =>
                    String(a.nombre || "").localeCompare(
                        String(b.nombre || ""),
                        "es",
                        {
                            sensitivity: "base",
                        }
                    )
                );

            setClientes(arr);
        } catch (error) {
            console.error(
                "Error cargando clientes:",
                error
            );

            setClientes([]);

            cambiarEstadoAlerta(true);

            cambiarAlerta({
                tipo: "error",
                mensaje:
                    "Error cargando clientes.",
            });
        } finally {
            setLoadingClientes(false);
        }
    };

    useEffect(() => {
        obtenerClientes();
    }, [users?.emp_id]);

    /* =====================================================
       FORMATO FECHA
    ===================================================== */

    const formatearFecha = (fecha) => {
        if (!fecha) {
            return "—";
        }

        if (fecha?.toDate) {
            return moment(
                fecha.toDate()
            ).format(
                "DD/MM/YYYY HH:mm"
            );
        }

        const fechaMoment = moment(fecha);

        return fechaMoment.isValid()
            ? fechaMoment.format(
                "DD/MM/YYYY HH:mm"
            )
            : "—";
    };

    /* =====================================================
       CONTAR DÍAS
    ===================================================== */

    const contarDias = (fecha) => {
        if (!fecha?.toDate) {
            return 0;
        }

        const fechaInicio = moment(
            fecha.toDate()
        ).startOf("day");

        const fechaActual = moment().startOf(
            "day"
        );

        const dias = fechaActual.diff(
            fechaInicio,
            "days"
        );

        return Math.max(dias, 0);
    };

    /* =====================================================
       TOTAL VALORIZADO
    ===================================================== */

    const valorizado = useMemo(() => {
        return inventario.reduce(
            (acc, item) =>
                acc +
                (Number(item.price) || 0),
            0
        );
    }, [inventario]);

    /* =====================================================
       PERMANENCIA PROMEDIO
    ===================================================== */

    const promedioDias = useMemo(() => {
        if (inventario.length === 0) {
            return 0;
        }

        const elementosConFecha =
            inventario.filter(
                (item) =>
                    item?.fecha_permanente?.toDate
            );

        if (
            elementosConFecha.length ===
            0
        ) {
            return 0;
        }

        const totalDias =
            elementosConFecha.reduce(
                (acc, item) =>
                    acc +
                    contarDias(
                        item.fecha_permanente
                    ),
                0
            );

        return (
            totalDias /
            elementosConFecha.length
        );
    }, [inventario]);

    /* =====================================================
       EN TRÁNSITO
    ===================================================== */

    const equiposEnTransito =
        useMemo(() => {
            return inventario.filter(
                (item) =>
                    normalizarTexto(
                        item.status
                    ).includes(
                        "TRANSITO"
                    )
            ).length;
        }, [inventario]);

    /* =====================================================
       EN SERVICIO TÉCNICO
    ===================================================== */

    const equiposEnST =
        useMemo(() => {
            return inventario.filter(
                (item) => {
                    const status =
                        normalizarTexto(
                            item.status
                        );

                    return (
                        status ===
                        "SERVICIO TECNICO" ||
                        status ===
                        "TRANSITO ST"
                    );
                }
            ).length;
        }, [inventario]);

    /* =====================================================
       BUSCAR INVENTARIO PERMANENTE
    ===================================================== */

    const buscarInventarioPorRut =
        async (rutSeleccionado) => {
            if (
                !users?.emp_id ||
                !rutSeleccionado
            ) {
                return;
            }

            cambiarEstadoAlerta(false);
            cambiarAlerta({});

            setLoadingInventario(true);

            try {
                /*
                 * Regla de negocio:
                 * el inventario del paciente se obtiene
                 * por su asociación PERMANENTE.
                 *
                 * No filtramos por status actual.
                 */
                const qInv = query(
                    collection(db, "status"),
                    where(
                        "emp_id",
                        "==",
                        users.emp_id
                    ),
                    where(
                        "r_permanente",
                        "==",
                        rutSeleccionado
                    )
                );

                const snap =
                    await getDocs(qInv);

                const arr =
                    snap.docs.map((d) => ({
                        ...d.data(),
                        id: d.id,
                    }));

                /*
                 * Equipos con mayor antigüedad
                 * permanente primero.
                 */
                arr.sort((a, b) => {
                    const aMs =
                        a?.fecha_permanente
                            ?.toMillis?.() ?? 0;

                    const bMs =
                        b?.fecha_permanente
                            ?.toMillis?.() ?? 0;

                    return aMs - bMs;
                });

                setInventario(arr);

                if (arr.length === 0) {
                    cambiarEstadoAlerta(true);

                    cambiarAlerta({
                        tipo: "info",
                        mensaje:
                            "Este paciente no tiene inventario permanente.",
                    });
                }
            } catch (error) {
                console.error(
                    "Error obteniendo inventario:",
                    error
                );

                setInventario([]);

                cambiarEstadoAlerta(true);

                cambiarAlerta({
                    tipo: "error",
                    mensaje:
                        "Error al obtener inventario del paciente.",
                });
            } finally {
                setLoadingInventario(false);
            }
        };

    /* =====================================================
       SELECCIÓN PACIENTE
    ===================================================== */

    const handleSelectItem = (item) => {
        if (
            !item ||
            typeof item !== "object"
        ) {
            cambiarEstadoAlerta(true);

            cambiarAlerta({
                tipo: "error",
                mensaje:
                    "Debes seleccionar un paciente del listado.",
            });

            return;
        }

        setRut(item.rut || "");
        setEntidad(item.nombre || "");

        setInventario([]);

        buscarInventarioPorRut(
            item.rut
        );
    };

    /* =====================================================
       LIMPIAR
    ===================================================== */

    const limpiaFormCte = () => {
        setRut("");
        setEntidad("");
        setInventario([]);

        cambiarEstadoAlerta(false);
        cambiarAlerta({});
    };

    /* =====================================================
       FORMATEAR PRECIO
    ===================================================== */

    const formatearPrecio = (precio) => {
        const numero =
            Number(precio);

        if (
            precio === null ||
            precio === undefined ||
            precio === "" ||
            Number.isNaN(numero)
        ) {
            return "—";
        }

        return numero.toLocaleString(
            "es-CL"
        );
    };

    /* =====================================================
       UBICACIÓN ACTUAL
    ===================================================== */

    const obtenerUbicacionActual = (
        item
    ) => {
        const status =
            normalizarTexto(
                item.status
            );

        /*
         * Si está físicamente en paciente,
         * el destino normalmente representa
         * al paciente actual.
         */
        if (
            status === "PACIENTE"
        ) {
            return (
                item.n_destino ||
                item.n_permanente ||
                entidad ||
                "Paciente"
            );
        }

        /*
         * Para estados intermedios usamos el
         * destino actual cuando está disponible.
         */
        if (
            item.n_destino
        ) {
            return item.n_destino;
        }

        if (
            item.n_origen
        ) {
            return item.n_origen;
        }

        return "—";
    };

    /* =====================================================
       KPI
    ===================================================== */

    const KpiCard = ({
        titulo,
        valor,
        icono,
        color,
        background,
        subtitulo,
    }) => (
        <Segment
            style={{
                margin: 0,
                border:
                    "1px solid #e5e9ef",
                borderRadius: 8,
                boxShadow:
                    "0 1px 3px rgba(9,30,66,0.06)",
                padding: "14px 16px",
                minHeight: 90,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "space-between",
                    gap: 10,
                }}
            >
                <div>
                    <div
                        style={{
                            color: "#8793a5",
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform:
                                "uppercase",
                            letterSpacing: 0.3,
                        }}
                    >
                        {titulo}
                    </div>

                    <div
                        style={{
                            marginTop: 4,
                            color: "#172b4d",
                            fontSize: 22,
                            lineHeight: 1.2,
                            fontWeight: 700,
                        }}
                    >
                        {valor}
                    </div>

                    {subtitulo && (
                        <div
                            style={{
                                color: "#98a1af",
                                fontSize: 10,
                                marginTop: 3,
                            }}
                        >
                            {subtitulo}
                        </div>
                    )}
                </div>

                <div
                    style={{
                        width: 38,
                        height: 38,
                        minWidth: 38,
                        borderRadius: 8,
                        background,
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                            "center",
                    }}
                >
                    <Icon
                        name={icono}
                        style={{
                            margin: 0,
                            color,
                            fontSize: 17,
                        }}
                    />
                </div>
            </div>
        </Segment>
    );

    /* =====================================================
       TABLA
    ===================================================== */

    const renderTabla = () => (
        <div
            style={{
                maxHeight: 580,
                overflow: "auto",
            }}
        >
            <Table
                celled
                compact
                selectable
                unstackable
                style={{
                    margin: 0,
                    border: 0,
                    borderRadius: 0,
                    minWidth: 1200,
                    fontSize: 12,
                }}
            >
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell
                            style={stickyTh}
                            textAlign="center"
                        >
                            N°
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            Equipo
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            Serie
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                            textAlign="center"
                        >
                            Cant.
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                            textAlign="right"
                        >
                            Precio
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            En paciente desde
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                            textAlign="right"
                        >
                            Días
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            Status actual
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            Ubicación actual
                        </Table.HeaderCell>

                        <Table.HeaderCell
                            style={stickyTh}
                        >
                            Movimiento
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {inventario.map(
                        (item, index) => (
                            <Table.Row
                                key={
                                    item.id || index
                                }
                            >
                                {/* N° */}

                                <Table.Cell
                                    textAlign="center"
                                    style={{
                                        color: "#6b778c",
                                    }}
                                >
                                    {index + 1}
                                </Table.Cell>

                                {/* EQUIPO */}

                                <Table.Cell
                                    style={{
                                        minWidth: 190,
                                        whiteSpace:
                                            "normal",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems:
                                                "flex-start",
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                minWidth: 30,
                                                borderRadius: 6,
                                                background:
                                                    "#eaf4fb",
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                            }}
                                        >
                                            <Icon
                                                name="archive"
                                                style={{
                                                    margin: 0,
                                                    color:
                                                        "#2185d0",
                                                    fontSize: 12,
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <div
                                                style={{
                                                    color:
                                                        "#172b4d",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {item.tipo ||
                                                    "—"}
                                            </div>

                                            {(item.marca ||
                                                item.modelo) && (
                                                    <div
                                                        style={{
                                                            marginTop: 2,
                                                            color:
                                                                "#8793a5",
                                                            fontSize: 11,
                                                        }}
                                                    >
                                                        {[
                                                            item.marca,
                                                            item.modelo,
                                                        ]
                                                            .filter(
                                                                Boolean
                                                            )
                                                            .join(" ")}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </Table.Cell>

                                {/* SERIE */}

                                <Table.Cell>
                                    <span
                                        style={{
                                            color: "#42526e",
                                            fontWeight: 600,
                                            whiteSpace:
                                                "nowrap",
                                        }}
                                    >
                                        {item.serie ||
                                            "—"}
                                    </span>
                                </Table.Cell>

                                {/* CANTIDAD */}

                                <Table.Cell
                                    textAlign="center"
                                >
                                    1
                                </Table.Cell>

                                {/* PRECIO */}

                                <Table.Cell
                                    textAlign="right"
                                    style={{
                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {formatearPrecio(
                                        item.price
                                    )}
                                </Table.Cell>

                                {/* FECHA PERMANENTE */}

                                <Table.Cell
                                    style={{
                                        whiteSpace:
                                            "nowrap",
                                        color: "#6b778c",
                                    }}
                                >
                                    <Icon
                                        name="calendar alternate outline"
                                        style={{
                                            color: "#98a1af",
                                        }}
                                    />

                                    {formatearFecha(
                                        item.fecha_permanente
                                    )}
                                </Table.Cell>

                                {/* DÍAS */}

                                <Table.Cell
                                    textAlign="right"
                                >
                                    <Label
                                        basic
                                        size="tiny"
                                        style={{
                                            margin: 0,
                                            color: "#42526e",
                                        }}
                                    >
                                        {Math.floor(
                                            contarDias(
                                                item.fecha_permanente
                                            )
                                        )}
                                    </Label>
                                </Table.Cell>

                                {/* STATUS */}

                                <Table.Cell>
                                    <StatusBadge
                                        status={
                                            item.status
                                        }
                                    />
                                </Table.Cell>

                                {/* UBICACIÓN */}

                                <Table.Cell
                                    style={{
                                        minWidth: 180,
                                        whiteSpace:
                                            "normal",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 6,
                                            alignItems:
                                                "flex-start",
                                        }}
                                    >
                                        <Icon
                                            name="map marker alternate"
                                            style={{
                                                color:
                                                    "#f2711c",
                                                marginTop: 2,
                                            }}
                                        />

                                        <span
                                            style={{
                                                color:
                                                    "#42526e",
                                            }}
                                        >
                                            {obtenerUbicacionActual(
                                                item
                                            )}
                                        </span>
                                    </div>
                                </Table.Cell>

                                {/* MOVIMIENTO */}

                                <Table.Cell
                                    style={{
                                        minWidth: 180,
                                        whiteSpace:
                                            "normal",
                                        color: "#6b778c",
                                    }}
                                >
                                    {item.tipoinout ||
                                        "—"}
                                </Table.Cell>
                            </Table.Row>
                        )
                    )}
                </Table.Body>
            </Table>
        </div>
    );

    /* =====================================================
       CONTENIDO
    ===================================================== */

    const renderContenido = () => {
        if (loadingInventario) {
            return (
                <Message
                    icon
                    info
                    style={{
                        margin: 16,
                    }}
                >
                    <Icon
                        name="spinner"
                        loading
                    />

                    <Message.Content>
                        <Message.Header>
                            Cargando inventario
                        </Message.Header>

                        Consultando los equipos
                        asignados permanentemente al
                        paciente.
                    </Message.Content>
                </Message>
            );
        }

        if (!rut) {
            return (
                <div
                    style={{
                        padding: 16,
                    }}
                >
                    <Message info icon>
                        <Icon name="search" />

                        <Message.Content>
                            <Message.Header>
                                Selecciona un paciente
                            </Message.Header>

                            Escribe para filtrar por
                            RUT o nombre y selecciona
                            un paciente desde el
                            listado.
                        </Message.Content>
                    </Message>
                </div>
            );
        }

        if (
            rut &&
            inventario.length === 0
        ) {
            return (
                <div
                    style={{
                        padding: 16,
                    }}
                >
                    <Message warning icon>
                        <Icon name="archive" />

                        <Message.Content>
                            <Message.Header>
                                Sin inventario permanente
                            </Message.Header>

                            No existen equipos
                            asociados permanentemente
                            a este paciente.
                        </Message.Content>
                    </Message>
                </div>
            );
        }

        return renderTabla();
    };

    /* =====================================================
       RETURN
    ===================================================== */

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f8fafc",
                paddingBottom: 40,
            }}
        >
            <Container
                fluid
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                    padding: "20px 16px",
                }}
            >
                {/* =================================================
            CABECERA
        ================================================= */}

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                            "space-between",
                        flexWrap: "wrap",
                        gap: 14,
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 13,
                        }}
                    >
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                minWidth: 48,
                                borderRadius: 9,
                                background: "#e5f7f7",
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                    "center",
                            }}
                        >
                            <Icon
                                name="archive"
                                style={{
                                    margin: 0,
                                    color: "#0d9da5",
                                    fontSize: 21,
                                }}
                            />
                        </div>

                        <div>
                            <Header
                                as="h2"
                                style={{
                                    margin: 0,
                                    color: "#172b4d",
                                    fontSize: 22,
                                    fontWeight: 700,
                                }}
                            >
                                Inventario por Paciente
                            </Header>

                            <div
                                style={{
                                    marginTop: 3,
                                    color: "#8793a5",
                                    fontSize: 12,
                                }}
                            >
                                Equipos asociados
                                permanentemente al
                                paciente y su ubicación
                                actual.
                            </div>
                        </div>
                    </div>
                </div>

                {/* =================================================
            BUSCADOR
        ================================================= */}

                <Segment
                    style={{
                        marginBottom: 18,
                        padding: 0,
                        overflow: "visible",
                        border:
                            "1px solid #e5e9ef",
                        borderRadius: 8,
                        boxShadow:
                            "0 1px 3px rgba(9,30,66,0.08)",
                    }}
                >
                    <div
                        style={{
                            padding:
                                "13px 17px",
                            borderBottom:
                                "1px solid #e5e9ef",
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: 8,
                                color: "#253858",
                                fontWeight: 700,
                            }}
                        >
                            <Icon
                                name="search"
                                style={{
                                    color: "#0d9da5",
                                }}
                            />

                            Buscar paciente
                        </div>

                        {rut && (
                            <Label
                                size="small"
                                style={{
                                    margin: 0,
                                    color: "#0d9da5",
                                    background:
                                        "#e5f7f7",
                                }}
                            >
                                <Icon name="check circle" />

                                Paciente seleccionado
                            </Label>
                        )}
                    </div>

                    <div
                        style={{
                            padding: 17,
                        }}
                    >
                        <Grid
                            stackable
                            verticalAlign="middle"
                        >
                            <Grid.Row>
                                <Grid.Column
                                    computer={10}
                                    tablet={10}
                                    mobile={16}
                                >
                                    <div
                                        style={{
                                            color: "#6b778c",
                                            fontSize: 11,
                                            fontWeight: 600,
                                            marginBottom: 6,
                                        }}
                                    >
                                        RUT o nombre
                                    </div>

                                    <BuscadorInput
                                        items={clientes}
                                        onSelectItem={
                                            handleSelectItem
                                        }
                                        limpiaFormCte={
                                            limpiaFormCte
                                        }
                                        confirmar={
                                            confirmar
                                        }
                                        rut={rut}
                                    />

                                    <div
                                        style={{
                                            color: "#98a1af",
                                            fontSize: 11,
                                            marginTop: 6,
                                        }}
                                    >
                                        <Icon name="info circle" />

                                        Seleccione un paciente
                                        para consultar los
                                        equipos que tiene
                                        asignados
                                        permanentemente.
                                    </div>
                                </Grid.Column>

                                <Grid.Column
                                    computer={6}
                                    tablet={6}
                                    mobile={16}
                                >
                                    <div
                                        style={{
                                            minHeight: 62,
                                            padding:
                                                "11px 13px",
                                            border:
                                                "1px solid #e5e9ef",
                                            borderRadius: 7,
                                            background:
                                                "#f8fafc",
                                            display: "flex",
                                            alignItems:
                                                "center",
                                        }}
                                    >
                                        {rut ? (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        minWidth: 36,
                                                        borderRadius:
                                                            7,
                                                        background:
                                                            "#e5f7f7",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                    }}
                                                >
                                                    <Icon
                                                        name="user"
                                                        style={{
                                                            margin: 0,
                                                            color:
                                                                "#0d9da5",
                                                        }}
                                                    />
                                                </div>

                                                <div
                                                    style={{
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            color:
                                                                "#172b4d",
                                                            fontWeight:
                                                                700,
                                                            fontSize: 14,
                                                            whiteSpace:
                                                                "nowrap",
                                                            overflow:
                                                                "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                        }}
                                                    >
                                                        {entidad ||
                                                            "Paciente"}
                                                    </div>

                                                    <div
                                                        style={{
                                                            marginTop: 3,
                                                            color:
                                                                "#6b778c",
                                                            fontSize: 11,
                                                        }}
                                                    >
                                                        RUT:{" "}
                                                        <strong>
                                                            {rut}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 7,
                                                    color:
                                                        "#8793a5",
                                                    fontSize: 12,
                                                }}
                                            >
                                                <Icon name="user outline" />

                                                {loadingClientes
                                                    ? "Cargando pacientes..."
                                                    : "Sin paciente seleccionado"}
                                            </div>
                                        )}
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </div>
                </Segment>

                {/* =================================================
            KPIs
        ================================================= */}

                {rut && (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(165px, 1fr))",
                            gap: 12,
                            marginBottom: 18,
                        }}
                    >
                        <KpiCard
                            titulo="Equipos asignados"
                            valor={
                                inventario.length
                            }
                            icono="archive"
                            color="#2185d0"
                            background="#eaf4fb"
                            subtitulo="Inventario permanente"
                        />

                        <KpiCard
                            titulo="Total valorizado"
                            valor={`$ ${valorizado.toLocaleString(
                                "es-CL"
                            )}`}
                            icono="money bill alternate outline"
                            color="#21ba45"
                            background="#edf8ef"
                            subtitulo="Valor registrado"
                        />

                        <KpiCard
                            titulo="Permanencia promedio"
                            valor={`${Math.floor(
                                promedioDias
                            )} días`}
                            icono="calendar alternate outline"
                            color="#f2711c"
                            background="#fff1e8"
                            subtitulo="Desde asignación"
                        />

                        <KpiCard
                            titulo="En tránsito"
                            valor={
                                equiposEnTransito
                            }
                            icono="truck"
                            color="#f2711c"
                            background="#fff1e8"
                            subtitulo="Ubicación temporal"
                        />

                        <KpiCard
                            titulo="Servicio Técnico"
                            valor={
                                equiposEnST
                            }
                            icono="wrench"
                            color="#6435c9"
                            background="#f0ebfb"
                            subtitulo="Incluye tránsito ST"
                        />
                    </div>
                )}

                {/* =================================================
            RESULTADOS
        ================================================= */}

                <Segment
                    style={{
                        padding: 0,
                        overflow: "hidden",
                        border:
                            "1px solid #e5e9ef",
                        borderRadius: 8,
                        boxShadow:
                            "0 1px 3px rgba(9,30,66,0.08)",
                    }}
                >
                    {/* HEADER */}

                    <div
                        style={{
                            padding:
                                "13px 17px",
                            borderBottom:
                                "1px solid #e5e9ef",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: 8,
                                color: "#253858",
                                fontWeight: 700,
                            }}
                        >
                            <Icon
                                name="list alternate outline"
                                style={{
                                    color: "#2185d0",
                                }}
                            />

                            Inventario permanente
                        </div>

                        <Label
                            basic
                            size="small"
                            style={{
                                margin: 0,
                                color: "#6b778c",
                            }}
                        >
                            Total:{" "}
                            <strong>
                                {inventario.length}
                            </strong>
                        </Label>
                    </div>

                    {renderContenido()}

                    {/* FOOTER */}

                    <div
                        style={{
                            padding:
                                "10px 16px",
                            background:
                                "#fbfcfd",
                            borderTop:
                                "1px solid #e5e9ef",
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            gap: 10,
                            flexWrap: "wrap",
                            color: "#8793a5",
                            fontSize: 11,
                        }}
                    >
                        <span>
                            Asociación permanente por
                            paciente
                        </span>

                        <span>
                            {inventario.length}{" "}
                            equipo(s)
                        </span>
                    </div>
                </Segment>

                {/* =================================================
            ALERTA
        ================================================= */}

                <Alerta
                    tipo={alerta.tipo}
                    mensaje={alerta.mensaje}
                    estadoAlerta={
                        estadoAlerta
                    }
                    cambiarEstadoAlerta={
                        cambiarEstadoAlerta
                    }
                />
            </Container>
        </div>
    );
};

export default ReporteInvCliente;